import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { FileText, Camera, Users, Heart, BarChart3, ChevronRight, Sparkles, ShoppingBag, Handshake, MessageSquareText, Zap, ShieldAlert } from "lucide-react";

export default function StudioPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/studio/overview");
        setStats(data);
      } catch {
        navigate("/profile/access");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  if (loading) {
    return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  }

  const m = stats?.metrics || {};
  // iter79 Batch 4: 8-Metriken-Overview (Spec S15)
  const statCards = [
    { icon: FileText, label: t("studio.stat_posts"), value: m.active_posts || 0, testId: "stat-posts" },
    { icon: Camera, label: t("studio.stat_stories"), value: m.active_stories || 0, testId: "stat-stories" },
    { icon: Sparkles, label: t("studio.stat_paid_media"), value: m.published_paid_media || 0, testId: "stat-paid-media" },
    { icon: ShoppingBag, label: t("studio.stat_plans"), value: m.active_subscription_plans || 0, testId: "stat-plans" },
    { icon: Handshake, label: t("studio.stat_mp_offers"), value: m.open_mp_offers || 0, testId: "stat-mp-offers" },
    { icon: MessageSquareText, label: t("studio.stat_stx_offers"), value: m.open_stx_offers || 0, testId: "stat-stx-offers" },
    { icon: Zap, label: t("studio.stat_boost"), value: m.active_boost || 0, testId: "stat-boost" },
    { icon: ShieldAlert, label: t("studio.stat_moderation"), value: m.open_moderation_cases || 0, testId: "stat-moderation" },
  ];

  const navItems = [
    { label: t("studio.social"), desc: t("studio.nav_social_desc"), path: "/studio/social", testId: "studio-nav-social" },
    { label: t("studio.nav_fans_label"), desc: t("studio.nav_fans_desc"), path: "/studio/fans", testId: "studio-nav-fans" },
    { label: t("studio.monetization"), desc: t("studio.nav_monetization_desc"), path: "/studio/monetization", testId: "studio-nav-monetization" },
    { label: t("studio.nav_helper_label"), desc: t("studio.nav_helper_desc"), path: "/studio/helper", testId: "studio-nav-helper" },
    { label: "Watch-Analytics", desc: "Impressions, Engagement & Retention deiner Beiträge", path: "/studio/watch-analytics", testId: "studio-nav-watch-analytics" },
    { label: t("studio.nav_listing_label"), desc: t("studio.nav_listing_desc"), path: "/listing/new", testId: "studio-nav-listing" },
    { label: t("studio.nav_my_listings_label"), desc: t("studio.nav_my_listings_desc"), path: "/studio/listings", testId: "studio-nav-my-listings" },
    { label: t("studio.nav_shipments_label"), desc: t("studio.nav_shipments_desc"), path: "/studio/shipments", testId: "studio-nav-shipments" },
    { label: t("studio.nav_sales_label"), desc: t("studio.nav_sales_desc"), path: "/profile/sales", testId: "studio-nav-sales" },
    { label: "STX-Digital-Inhalte", desc: "Freigabe & Release-Status deiner STX-Digital-Käufer", path: "/studio/stx-digital", testId: "studio-nav-stx-digital" },
    { label: t("studio.nav_rescue_label"), desc: t("studio.nav_rescue_desc"), path: "/seller/rescue", testId: "studio-nav-rescue" },
    { label: t("studio.nav_preview_label"), desc: t("studio.nav_preview_desc"), path: "/studio/preview", testId: "studio-nav-preview" },
    { label: t("studio.nav_facets_label"), desc: t("studio.nav_facets_desc"), path: "/profile/facets", testId: "studio-nav-facets" },
    { label: t("studio.nav_delay_label"), desc: t("studio.nav_delay_desc"), path: "/studio/processing-delay", testId: "studio-nav-delay" },
    { label: t("studio.nav_profile_label"), desc: t("studio.nav_profile_desc"), path: "/profile/edit", testId: "studio-nav-profile" },
  ];

  return (
    <div data-testid="studio-page" className="pb-4 w-full max-w-5xl 2xl:max-w-full mx-auto">
      <div className="px-4 pt-6 pb-4 border-b border-[#E5E5E5]">
        <h1 className="text-lg font-bold text-[#0A0A0A]">{t("studio.title")}</h1>
        <p className="text-xs text-[#999] mt-0.5">{t("studio.subtitle")}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-px bg-[#E5E5E5] border-b border-[#E5E5E5]">
        {statCards.map(({ icon: Icon, label, value, testId }) => (
          <div key={testId} data-testid={testId} className="bg-white p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon size={14} className="text-[#999]" />
              <span className="text-[10px] text-[#999] uppercase tracking-wider">{label}</span>
            </div>
            <p className="text-2xl font-bold text-[#0A0A0A]">{value}</p>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="divide-y divide-[#E5E5E5]">
        {navItems.map(({ label, desc, path, testId }) => (
          <button
            key={path}
            data-testid={testId}
            onClick={() => navigate(path)}
            className="w-full flex items-center gap-3 px-4 py-4 hover:bg-[#FAFAFA] transition-colors"
          >
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-[#0A0A0A]">{label}</p>
              <p className="text-xs text-[#999] mt-0.5">{desc}</p>
            </div>
            <ChevronRight size={16} className="text-[#CCC]" />
          </button>
        ))}
      </div>
    </div>
  );
}
